import { Dialog, OdinIcon } from '@myosh/odin-components'
import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import {
  useDeleteTrainingMatrixReportMutation,
  useTrainingMatrixReportsQuery
} from '../../../../API/rtk-query-api/store/tmsApi'
import ActionIcon, { type ActionIconProps } from '../../../UI/action-icon/action-icon'
import LoadingSpinner from '../../../UI/loading-spinner'
import type { Page } from '../../training-matrix/view_dropdown_data'
import ScheduledExport from './ScheduledReport'
import './scheduleExport.scss'

interface ScheduledReportListProps {
  isVisible: boolean
  selectedPage: Page
  hide: () => void
}

function ScheduledReportList({ isVisible, selectedPage, hide }: ScheduledReportListProps) {
  const [isModalVisible, setIsModalVisible] = useState(isVisible)
  const [showReportModal, setShowReportModal] = useState(false)
  const [reportToDeleteID, setReportToDeleteID] = useState<string>()
  const reportEditID = useRef<string | undefined>(undefined)

  const { data: reports, isFetching: isFetchingReports } = useTrainingMatrixReportsQuery({})
  const [deleteReport] = useDeleteTrainingMatrixReportMutation()

  useEffect(() => {
    if (isVisible !== isModalVisible) {
      setIsModalVisible(isVisible)
    }
  }, [isVisible])

  const onHideModal = () => {
    setReportToDeleteID(undefined)
    setIsModalVisible(false)
    hide()
  }

  const handleShowReportModal = () => setShowReportModal(true)
  const handleCloseReportModal = () => {
    setShowReportModal(false)
    if (reportEditID.current) {
      reportEditID.current = undefined
    }
  }
  const onEditReportClick = (reportId: string | undefined) => {
    setShowReportModal(true)
    if (reportId) {
      reportEditID.current = reportId
    }
  }

  const onDeleteReport = (reportId: string | undefined) => {
    if (reportId) {
      toast.promise(
        deleteReport({ id: reportId })
          .unwrap()
          .then(() => {
            setReportToDeleteID(undefined)
          }),
        {
          loading: 'Deleting Report...',
          success: 'Report Deleted',
          error: 'Failed To Delete Report'
        }
      )
    }
  }

  return (
    <>
      {isModalVisible && (
        <Dialog header='Scheduled Exports' visible={isModalVisible} onClose={onHideModal}>
          <>
            <button onClick={handleShowReportModal} className='p-button icon-text-btn' aria-label='New Report'>
              <OdinIcon icon='Plus' />
              <span>New Report</span>
            </button>
            <div className='schedule-report-list'>
              {isFetchingReports && (
                <div style={{ display: 'grid', placeItems: 'center' }}>
                  <LoadingSpinner />
                </div>
              )}
              {!isFetchingReports &&
                reports?.data?.map((report, index) => {
                  const isEven = index % 2 === 1
                  if (reportToDeleteID === report.id) {
                    return (
                      <ScheduleReportListItem
                        title={`Delete "${report.name}"?`}
                        actionItems={[
                          { icon: 'Check', onClick: () => onDeleteReport(report.id), iconTheme: 'default' },
                          { icon: 'X', onClick: () => setReportToDeleteID(undefined), iconTheme: 'default' }
                        ]}
                        className='delete-confirmation-item'
                        key={report.id}
                      />
                    )
                  }

                  return (
                    <ScheduleReportListItem
                      title={report.name}
                      actionItems={[
                        { icon: 'Pencil', onClick: () => onEditReportClick(report.id) },
                        { icon: 'Trash', onClick: () => setReportToDeleteID(report.id) }
                      ]}
                      isEven={isEven}
                      key={report.id}
                    />
                  )
                })}
            </div>
          </>
        </Dialog>
      )}
      <ScheduledExport
        isVisible={showReportModal}
        id={reportEditID.current}
        selectedPage={selectedPage}
        hide={handleCloseReportModal}
      />
    </>
  )
}

export default ScheduledReportList

interface ScheduleReportListItemProps {
  title: string
  actionItems: Array<ActionIconProps>
  isEven?: boolean
  className?: string
}

function ScheduleReportListItem({ title, actionItems, isEven, className }: ScheduleReportListItemProps) {
  const defaultClass = 'schedule-report-list__item'
  const itemClass = className ? `${defaultClass} ${className}` : defaultClass

  return (
    <div className={itemClass} data-even={isEven}>
      <p title={title}>{title}</p>
      <div className='schedule-report-list__item-actions'>
        {actionItems.map((item, index) => {
          return <ActionIcon {...item} key={index} />
        })}
      </div>
    </div>
  )
}
